/* Copyright (c) 2017 Backpack Health LLC */
import * as _ from 'lodash';

import store from '../../redux/reduxStore_authorizedApp';

import actionNames from './actionNames';
import { getActiveProfile } from './reduxSelectors';
import ProfileService from '../../../apiServices/ProfileService';
import { isAbleToMakeRequest } from '../../../utilities/MobileUtil';
import { lastAccessedProfiles, activeProfile } from '../../../utilities/LocalStorage';
import { actions as ownerActions } from '../../redux/ducks/owners';
import { actions as groupActions } from '../../redux/ducks/group';
import { actions as consentActions } from '../../redux/ducks/consent';
import { actions as integrationsActions } from '../../redux/ducks/integrations';
import GroupService from '../../../apiServices/GroupService';

const actions = {
  addProfileAction: profileData =>
    _.assign(
      {},
      {
        type: actionNames.addProfile,
        payload: profileData,
      }
    ),

  updateProfileAction: profileData =>
    _.assign(
      {},
      {
        type: actionNames.updateProfile,
        payload: profileData,
      }
    ),

  updateProfileTimestampAction: profileId =>
    _.assign(
      {},
      {
        type: actionNames.updateProfileTimestamp,
        payload: profileId,
      }
    ),

  receivedProfileCollectionAction: profileCollection =>
    _.assign(
      {},
      {
        type: actionNames.receivedProfileCollection,
        payload: profileCollection,
      }
    ),

  removeProfileAction: profileId =>
    _.assign(
      {},
      {
        type: actionNames.removeProfile,
        payload: {
          id: profileId,
        },
      }
    ),

  setNextProfileColorAction: colorData =>
    _.assign(
      {},
      {
        type: actionNames.setNextProfileColor,
        payload: colorData,
      }
    ),
};

function setActiveProfile(profileId) {
  activeProfile.set(profileId);
  window.scrollTo(0, 0);
  return _.assign(
    {},
    {
      type: actionNames.setActiveProfile,
      payload: {
        id: profileId,
      },
    }
  );
}

export function setCurrentColorTheme(colorTheme) {
  return _.assign(
    {},
    {
      type: actionNames.setCurrentColorTheme,
      payload: colorTheme.id,
    }
  );
}

export function setColorThemeToActiveProfile() {
  const state = store.getState();
  const profile = getActiveProfile(state);

  store.dispatch(setCurrentColorTheme({ id: profile.data.color_theme_id }));
}

function getProfile(profileId, callback) {
  ProfileService.get(profileId, (apiError, profile) => {
    if (!apiError) {
      store.dispatch(actions.addProfileAction(profile.data));
    }
    callback && callback(apiError, profile);
  });
}

function updateProfile(profileData, callback) {
  if (isAbleToMakeRequest()) {
    const profileWithValidValues = _.pickBy(profileData, val => val !== null);

    ProfileService.update(profileWithValidValues, apiError => {
      if (!apiError) {
        store.dispatch(actions.updateProfileAction(profileData));
        store.dispatch(consentActions.requestAllConsents(profileData.id)); // need to call this if date of birth changed
      }

      if (callback) { callback(apiError); }
    });
  }
}

function updateProfileTimestamp(profileId) {
  store.dispatch(actions.updateProfileTimestampAction(profileId));
}

function requestAllProfiles() {
  return new Promise((res, rej) => {
    ProfileService.getAll((apiError, profileCollection) => {
      if (apiError) {
        rej(apiError);
        return;
      }

      store.dispatch(
        actions.receivedProfileCollectionAction(profileCollection)
      );

      res(profileCollection);
    });
  });
}

function createDependentProfile(profileData, callback) {
  if (isAbleToMakeRequest()) {
    ProfileService.createDependentProfile(profileData, (apiError, profile) => {
      if (!apiError) {
        store.dispatch(actions.addProfileAction(profile.data));
      }
      callback(apiError, profile);
    });
  }
}

function deleteDependentProfile(profileId, callback) {
  if (isAbleToMakeRequest()) {
    ProfileService.deleteDependentProfile(profileId, apiError => {
      callback(apiError);
      store.dispatch(actions.removeProfileAction(profileId));
    });
  }
}

function requestNextProfileColor(callback = () => {}) {
  ProfileService.getNextProfileColorTheme((apiError, colorTheme) => {
    store.dispatch(actions.setNextProfileColorAction(colorTheme));
    callback(apiError, colorTheme);
  });
}

function deleteAvatar(profileData) {
  ProfileService.delAvatar(profileData, apiError => {
    if (apiError) {
      // TODO: Show error message
      // console.log(apiError.messageKey());
    } else {
      getProfile(profileData.id);
    }
  });
}

function provideConsent(profileId, policyIdList) {
  return ProfileService.provideConsent(profileId, policyIdList);
}

function withdrawConsent(profileId, policyIdList) {
  return ProfileService.withdrawConsent(profileId, policyIdList);
}

function withdrawGroupConsentAndLeave(profileId, groupIds, policyIdList) {
  const withdrawPromises = groupIds.map(gId => ProfileService.withdrawGroupConsent(profileId, gId, policyIdList));
  const leavePromises = groupIds.map(gId => GroupService.leaveGroup(profileId, gId));
  return Promise.all([...withdrawPromises, ...leavePromises]);
}

function switchToProfile(profileData) {
  return (dispatch) => {
    return new Promise((res) => {
      // const state = getState();
      // const mainProfileId = getMainProfileId(state);
      const profileId = profileData.id;

      dispatch(setActiveProfile(profileData.id));
      setColorThemeToActiveProfile();

      setTimeout(() => {
        // dispatch(actionItemActions.requestActionItems(null, profileId));
        // dispatch(consentActions.requestAllConsents(profileId));

        // this seems like a lot of possibly unneccessary API calls to make for every profile change
        // and they are pre-emptive at this point

        // ShareCardDefinitionsActions.load(profileId);
        dispatch(ownerActions.requestOwnersForProfile(profileId));
        dispatch(groupActions.requestGroups(profileId));
        dispatch(integrationsActions.requestIntegrations(profileId));

        lastAccessedProfiles.update(profileId);

        updateProfileTimestamp(profileId);
        res();
      }, 250);
    });
  };
}

const ProfileActions = {
  setActiveProfile,
  setCurrentColorTheme: colorTheme =>
    store.dispatch(setCurrentColorTheme(colorTheme)),
  setColorThemeToActiveProfile,
  receiveProfile: actions.addProfileAction,

  requestAllProfiles,
  requestNextProfileColor,
  updateProfileTimestamp,
  switchToProfile,

  dispatch: {
    setActiveProfile: profileData => {
      const profileId = profileData.id;
      store.dispatch(setActiveProfile(profileId));
    },
  },

  api: {
    getProfile,
    updateProfile,
    createDependentProfile,
    deleteDependentProfile,
    deleteAvatar,
    provideConsent,
    withdrawConsent,
    withdrawGroupConsentAndLeave,
  },
};

export default ProfileActions;
